/**
 * FilterPill — "Filtered by: …" beside the sort pill.
 *
 * Before this the only sign of an active filter was a blue tint on the
 * funnel icon, easy to miss — worst on tables that open pre-filtered,
 * where the list looks complete but is not. The pill names what the
 * table is filtered by, in the same chrome as "Sorted by", and its
 * panel takes over the funnel menu: the active filters (each with its
 * own remove), reset all, and the filter-row toggle.
 *
 * Opens and closes like SortPill (absolute panel, outside click
 * closes) so the two pills behave as a pair. Removing a filter goes
 * through the same `setFilters` updater the reset already used.
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { styled } from '@mui/material/styles';
import { useSelector } from 'react-redux';
import { Toggle } from '@shared/ui';
import { ChevronDownIcon } from './icons';
import {
  PillRoot,
  PillTrigger,
  PillLabel,
  PillValue,
  PillPanel,
  PillPanelHeader,
  PillPanelTitle,
} from './pillStyles';
import { describeFilterValue, hasFilterValue } from './lib/describeFilter';

// Column names the trigger lists before folding the rest into "+N".
const NAMED_IN_TRIGGER = 2;

const ResetButton = styled('button')({
  padding: 0,
  border: 'none',
  background: 'none',
  fontFamily: 'inherit',
  fontSize: 12,
  fontWeight: 600,
  color: 'var(--qhs-brand)',
  cursor: 'pointer',
  '&:disabled': { color: 'var(--qhs-text-3)', cursor: 'default' },
});

const FilterList = styled('div')({
  maxHeight: 260,
  overflowY: 'auto',
  borderTop: '1px solid var(--qhs-border)',
  borderBottom: '1px solid var(--qhs-border)',
  padding: '4px 0',
  marginBottom: 10,
});

const FilterRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  gap: 8,
  padding: '7px 10px',
  borderRadius: 6,
  fontSize: 13,
  '&:hover': { background: 'var(--qhs-surface-hover)' },
});

const FilterName = styled('span')({ color: 'var(--qhs-text-3)', flexShrink: 0 });

const FilterValue = styled('span')({
  flex: 1,
  minWidth: 0,
  color: 'var(--qhs-text-1)',
  fontWeight: 600,
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
});

const RemoveButton = styled('button')({
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 20,
  height: 20,
  flexShrink: 0,
  padding: 0,
  border: 'none',
  borderRadius: 4,
  background: 'none',
  color: 'var(--qhs-text-3)',
  cursor: 'pointer',
  '&:hover': { color: 'var(--qhs-text-1)', background: 'var(--qhs-border)' },
  '& svg': { width: 12, height: 12 },
});

const EmptyNote = styled('div')({
  padding: '10px',
  fontSize: 13,
  color: 'var(--qhs-text-3)',
});

// A div, not a label: Toggle renders its own label, and a label inside
// a label fires the change twice.
const ToggleRow = styled('div')({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  gap: 8,
  padding: '2px 10px',
  fontSize: 13,
  color: 'var(--qhs-text-2)',
});

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    aria-hidden
  >
    <path d="M18 6 6 18M6 6l12 12" />
  </svg>
);

const RecentTitle = styled('div')({
  padding: '0 10px 4px',
  fontSize: 10.5,
  fontWeight: 700,
  letterSpacing: 0.6,
  textTransform: 'uppercase',
  color: 'var(--qhs-text-3)',
});

const RecentList = styled('div')({
  borderBottom: '1px solid var(--qhs-border)',
  padding: '0 0 6px',
  marginBottom: 10,
});

const RecentItem = styled('button')({
  display: 'block',
  width: '100%',
  padding: '7px 10px',
  border: 'none',
  borderRadius: 6,
  background: 'none',
  textAlign: 'left',
  fontFamily: 'inherit',
  fontSize: 13,
  color: 'var(--qhs-text-2)',
  whiteSpace: 'nowrap',
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  cursor: 'pointer',
  '&:hover': { background: 'var(--qhs-surface-hover)', color: 'var(--qhs-text-1)' },
});

const FilterPill = ({
  filter,
  setFilters,
  columns,
  showColumnFilters,
  onToggleShowFilters,
  // currentFilter => [{ filter, savedAt }]: the table's recent filters,
  // newest first, without the one passed in.
  getRecent,
}) => {
  const { tables = {}, scit, slit, formulas } = useSelector(state => state.i18n) || {};
  const [open, setOpen] = useState(false);
  const rootRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const handle = e => {
      if (rootRef.current && !rootRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handle);
    return () => document.removeEventListener('mousedown', handle);
  }, [open]);

  const describe = useCallback(
    entries => {
      const dict = { scit, slit, formulas, tables };
      return (entries || [])
        .filter(entry => hasFilterValue(entry?.value))
        .map(({ id, value }) => {
          const column = (columns || []).find(c => c.accessorKey === id);
          return {
            id,
            name: typeof column?.header === 'string' ? column.header : id,
            value: describeFilterValue(value, column?.meta?.qhsFilter, dict),
          };
        });
    },
    [columns, scit, slit, formulas, tables]
  );

  const active = useMemo(() => describe(filter), [describe, filter]);

  // Read while the panel is open, so it reflects what was just recorded.
  const recent = open && getRecent ? getRecent(filter) : [];

  const named = active
    .slice(0, NAMED_IN_TRIGGER)
    .map(entry => entry.name)
    .join(', ');
  const rest = active.length - NAMED_IN_TRIGGER;
  const triggerValue = rest > 0 ? `${named} +${rest}` : named;

  const removeFilter = id => setFilters(previous => previous.filter(entry => entry.id !== id));

  return (
    <PillRoot ref={rootRef}>
      <PillTrigger
        type="button"
        onClick={() => setOpen(o => !o)}
        aria-haspopup="dialog"
        aria-expanded={open}
      >
        <PillLabel>{tables.filteredBy || 'Filtered by: '}</PillLabel>
        {active.length > 0 ? (
          <PillValue $tone="brand" title={active.map(entry => entry.name).join(', ')}>
            {triggerValue}
          </PillValue>
        ) : (
          <PillValue $tone="muted">{tables.noFilters || 'None'}</PillValue>
        )}
        <span style={{ color: 'var(--qhs-text-3)', display: 'inline-flex' }}>
          <ChevronDownIcon />
        </span>
      </PillTrigger>

      {open && (
        <PillPanel role="dialog" aria-label={tables.activeFilters || 'Active filters'}>
          <PillPanelHeader>
            <PillPanelTitle>{tables.activeFilters || 'Active filters'}</PillPanelTitle>
            <ResetButton
              type="button"
              disabled={active.length === 0}
              onClick={() => {
                // Closes like the old funnel menu did: nothing is left
                // to act on in the panel once every filter is gone.
                setFilters(() => []);
                setOpen(false);
              }}
            >
              {tables.resetAllFilters || 'Reset all filters'}
            </ResetButton>
          </PillPanelHeader>

          <FilterList>
            {active.length === 0 && (
              <EmptyNote>{tables.noActiveFilters || 'No filters applied'}</EmptyNote>
            )}
            {active.map(entry => (
              <FilterRow key={entry.id}>
                <FilterName>{entry.name}</FilterName>
                <FilterValue title={entry.value}>{entry.value}</FilterValue>
                <RemoveButton
                  type="button"
                  aria-label={`${tables.removeFilter || 'Remove filter'}: ${entry.name}`}
                  onClick={() => removeFilter(entry.id)}
                >
                  <CloseIcon />
                </RemoveButton>
              </FilterRow>
            ))}
          </FilterList>

          {recent.length > 0 && (
            <RecentList>
              <RecentTitle>{tables.recentFilters || 'Recent'}</RecentTitle>
              {recent.map(entry => {
                const text = describe(entry.filter)
                  .map(item => `${item.name}: ${item.value}`)
                  .join(' · ');
                return (
                  <RecentItem
                    key={entry.savedAt}
                    type="button"
                    title={text}
                    onClick={() => {
                      setFilters(() => entry.filter);
                      setOpen(false);
                    }}
                  >
                    {text}
                  </RecentItem>
                );
              })}
            </RecentList>
          )}

          <ToggleRow>
            {tables.showFilters || 'Show filters'}
            <Toggle
              checked={showColumnFilters}
              onChange={e => onToggleShowFilters(e.target.checked)}
              aria-label={tables.showFilters || 'Show filters'}
            />
          </ToggleRow>
        </PillPanel>
      )}
    </PillRoot>
  );
};

export default FilterPill;
